import Vue from 'vue';
import Router from 'vue-router';
import EmptyRouterView from '@/components/EmptyRouterView/EmptyRouterView.vue';
import TokenService from '@/services/token.service';
import { store } from '@/store/store';
import { SUPPORTED_SU, checkAcceptingTerms } from '@/config/app.config';
import UserService from './services/user.service';
import LocalStoreService from '@/store/local.store';
import { getScreeningUnitNameFromID } from '@/config/app.config';
import moment from 'moment';


Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/:screening',
      component: EmptyRouterView, // must pass a component here
      children: [
        {
          path: '',
          name: 'home',
          redirect: 'login',
          meta: {
            public: true,  // Allow access to even if not logged in
            onlyWhenLoggedOut: false,
          },
        },
        {
          path: 'login',
          name: 'login',
          component: () => import('./views/ParticipantIdentification/ParticipantIdentification.vue'),
          meta: {
            public: true,  // Allow access to even if not logged in
            onlyWhenLoggedOut: true,
          },
        },
        {
          path: 'login/fit',
          name: 'login-fit',
          component: () => import('./views/ParticipantIdentification/ParticipantIdentification.vue'),
          meta: {
            public: true,  // Allow access to even if not logged in
            onlyWhenLoggedOut: true,
          },
        },
        {
          path: 'login/colonoscopy',
          name: 'login-colo',
          component: () => import('./views/ParticipantIdentification/ParticipantIdentification.vue'),
          meta: {
            public: true,  // Allow access to even if not logged in
            onlyWhenLoggedOut: true,
          },
        },
        {
          path: 'login/refusal',
          name: 'login-refusal',
          component: () => import('./views/ParticipantIdentification/ParticipantIdentification.vue'),
          meta: {
            public: true,  // Allow access to even if not logged in
            onlyWhenLoggedOut: true,
          },
        },
        {
          path: 'passwordModification',
          name: 'password-modification',
          component: () => import('./views/PasswordModification/PasswordModification.vue'),
        },
        {
          path: 'pro/login',
          name: 'pro-login',
          component: () => import('./views/UserAuthentication/UserAuthentication.vue'),
          meta: {
            public: true,  // Allow access to even if not logged in
            onlyWhenLoggedOut: true,
          },
        },
        {
          path: ':partId/initialChoice',
          name: 'initialChoice',
          component: () => import(/* webpackChunkName: "initialChoice" */ './views/InitialChoice/InitialChoice'),
        },
        {
          path: ':partId/inclusion',
          name: 'inclusion',
          // route level code-splitting
          // this generates a separate chunk (inclusion.[hash].js) for this route
          // which is lazy-loaded when the route is visited.
          component: () => import(/* webpackChunkName: "inclusion" */ './views/Inclusion/Inclusion.vue'),
        },
        {
          path: ':partId/alternativeQuestionnaire',
          name: 'alternativeQuestionnaire',
          component: () => import(
            /* webpackChunkName: "alternativeQuestionnaire" */
            './views/AlternativeQuestionnaire/AlternativeQuestionnaire'),
        },
        {
          path: ':partId/questionnary',
          name: 'questionnary',
          component: () => import(/* webpackChunkName: "questionnary" */ './views/Questionnary/Questionnary'),
        },
        {
          path: ':partId/medicalcontent',
          name: 'medicalcontent',
          component: () => import(/* webpackChunkName: "medicalcontent" */ './views/MedicalContent/MedicalContent'),
        },
        {
          path: ':partId/consent',
          name: 'consent',
          component: () => import(/* webpackChunkName: "consent" */ './views/Consent/Consent'),
        },
        {
          path: ':partId/recap',
          name: 'recap',
          component: () => import('./views/Recap/Recap'),
        },
        {
          path: 'confirmation',
          name: 'confirmation',
          component: () => import('./views/Confirmation/Confirmation.vue'),
        },
        {
          path: 'admin/translation',
          name: 'translation',
          component: () => import('./views/AdminTranslation/AdminTranslation.vue'),
          meta: {
            public: true,  // Allow access to even if not logged in
            onlyWhenLoggedOut: true,
          },
        },
      ],
    },
    {
      // Default behaviour if no screening is set in the URL
      path: '/',
      name: 'default',
      redirect: () => {
        const su = LocalStoreService.localItemKeys.screeningUnit;
        const redirectTo = LocalStoreService.itemExists(su) ?
          getScreeningUnitNameFromID(LocalStoreService.getItem(su)) + '/login' :
          `${process.env.VUE_APP_DEFAULT_ROUTE_PREFIX}/login`;
        return redirectTo;
      },
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  },
});

router.beforeEach((to, from, next) => {
  const isPublic = to.matched.some((record) => record.meta.public);
  const onlyWhenLoggedOut = to.matched.some((record) => record.meta.onlyWhenLoggedOut);
  const loggedIn = TokenService.tokenExists() && (store.state.isIdentified || store.state.isAuthenticated);
  const suName = to.params.screening;
  // Prevent the user to change pages without having the latest app version
  if (store.state.isAppLoaded) {
    if (!store.state.appVersion.isLatest) {
      window.location.reload();
    } else {
      // If it's the latest version, check also the last time we checked it
      const a = moment(store.state.appVersion.lastCheckDate);
      const b = moment();
      if (b.diff(a, 'days') > 0) {
        window.location.reload();
      }
    }
  }

  if (suName !== undefined) {
    // Redirect to homepage if it's not a supported SU
    if (!SUPPORTED_SU.includes(suName.toLocaleLowerCase())) {
      return next({
        name: 'default',
      });
    }
  }

  if (!isPublic && !loggedIn) {
    return next({
      path: '/',
      // query: { redirect: to.fullPath },  // Store the full path to redirect the user to after login
    });
  }

  // Do not allow user to visit login page or register page if they are logged in
  // Pro users should access to the login page in order to open the participant dossier
  if (loggedIn && onlyWhenLoggedOut && !store.state.isAuthenticated) {
    return next({
      name: 'home',
    });
  }

  // Check accepting terms of successfully logged in pro users
  if (loggedIn && store.state.isAuthenticated) {
    if (checkAcceptingTerms(from.params.screening)) {
      const termsacceptancedate = router.app.$getAcceptanceDate();
      if (!termsacceptancedate) {
        UserService.logout();
        return next({
          name: 'pro-login',
        });
      }
    }
  }

  // Do not allow to come back after confirmation
  // Go back to login page
  if (from.name === 'confirmation' && !!to.name) {
    if (['recap', 'questionnary', 'alternativeQuestionnaire'].includes(to.name)) {
      return next({
        name: 'home',
      });
    }
  }

  // Do not allow to access recap page if consent is not accepted
  if (from.name === 'consent' && to.name === 'recap' && !store.state.consentAccepted.valueOf()) {
    return next({ name: 'consent' });
  }
  next();
});

export default router;
